<template>
  <div class="medical-records-panel">
    <PatientSelectModal
      :visible.sync="showPatientModal"
      :patient-options="patientOptions"
      :loading="loadingPatients"
      @confirm="onPatientConfirmed"
    />

    <div v-if="!selectedPatient" class="medical-records-panel__empty text-center py-5">
      <p class="text-muted mb-3">{{ $t("prontuariosPage.selectPatientHint") }}</p>
      <b-button variant="primary" @click="openPatientModal">
        {{ $t("prontuariosPage.selectPatientButton") }}
      </b-button>
    </div>

    <template v-else>
      <div class="medical-records-panel__header d-flex flex-wrap align-items-center justify-content-between mb-3">
        <div>
          <span class="text-muted">{{ $t("prontuariosPage.selectedPatient") }}:</span>
          <strong class="ml-1">{{ selectedPatient.id }} — {{ selectedPatient.name }}</strong>
        </div>
        <b-button size="sm" variant="outline-primary" @click="openPatientModal">
          {{ $t("prontuariosPage.changePatient") }}
        </b-button>
      </div>

      <b-tabs v-model="activeTab" pills content-class="mt-3">
        <b-tab :title="$t('prontuariosPage.tabs.generalData.title')" lazy>
          <GeneralDataTab
            :entity-type="entityType"
            :entity-id="entityId"
            :patient="selectedPatient"
          />
        </b-tab>

        <b-tab :title="$t('prontuariosPage.tabs.anamnesis.title')" lazy>
          <AnamnesisTab
            :entity-type="entityType"
            :entity-id="entityId"
            :patient="selectedPatient"
          />
        </b-tab>

        <b-tab :title="$t('prontuariosPage.tabs.therapeuticPlan.title')" lazy>
          <TherapeuticPlanTab
            :entity-type="entityType"
            :entity-id="entityId"
            :patient="selectedPatient"
          />
        </b-tab>

        <b-tab :title="$t('prontuariosPage.tabs.reports.title')" lazy>
          <ReportsTab
            :entity-type="entityType"
            :entity-id="entityId"
            :patient="selectedPatient"
          />
        </b-tab>
      </b-tabs>
    </template>
  </div>
</template>

<script>
import { http } from "@/helpers/easyindustriaapi/config";
import PatientSelectModal from "./PatientSelectModal.vue";
import GeneralDataTab from "./tabs/GeneralDataTab.vue";
import AnamnesisTab from "./tabs/AnamnesisTab.vue";
import TherapeuticPlanTab from "./tabs/TherapeuticPlanTab.vue";
import ReportsTab from "./tabs/ReportsTab.vue";

export default {
  name: "MedicalRecordsPanel",
  components: {
    PatientSelectModal,
    GeneralDataTab,
    AnamnesisTab,
    TherapeuticPlanTab,
    ReportsTab,
  },
  props: {
    entityType: {
      type: String,
      default: "",
    },
    entityId: {
      type: [Number, String],
      default: null,
    },
    patient: {
      type: Object,
      default: null,
    },
    requirePatientPicker: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      activeTab: 0,
      showPatientModal: false,
      selectedPatient: null,
      patientOptions: [],
      loadingPatients: false,
      currentEmpresa: null,
    };
  },
  computed: {
    entityContextKey() {
      return `${this.entityType}|${this.entityId ?? ""}`;
    },
  },
  watch: {
    entityContextKey(newKey, oldKey) {
      if (oldKey != null && newKey !== oldKey) {
        this.syncSelectedPatient({ contextChanged: true });
      }
    },
    patient: {
      handler() {
        this.syncSelectedPatient({ contextChanged: true });
      },
      deep: true,
    },
  },
  mounted() {
    try {
      this.currentEmpresa = JSON.parse(localStorage.getItem("currentEmpresa"));
    } catch {
      this.currentEmpresa = null;
    }

    this.syncSelectedPatient({ contextChanged: false });
  },
  methods: {
    normalizePatient(raw) {
      const id = raw.id;
      const name = raw.name || raw.nome || "";
      return {
        id,
        name,
        nome: name,
        label: `${String(id)} — ${name}`,
      };
    },
    syncSelectedPatient({ contextChanged = false } = {}) {
      if (this.patient && this.patient.id) {
        const normalized = this.normalizePatient(this.patient);
        if (
          !contextChanged &&
          this.selectedPatient &&
          this.selectedPatient.id === normalized.id
        ) {
          return;
        }
        this.selectedPatient = normalized;
        this.showPatientModal = false;
        this.activeTab = 0;
        return;
      }

      if (contextChanged) {
        this.selectedPatient = null;
        this.activeTab = 0;
        this.showPatientModal = false;
        this.patientOptions = [];
        if (this.requirePatientPicker) {
          this.$nextTick(() => this.openPatientModal());
        }
        return;
      }

      if (this.requirePatientPicker && !this.selectedPatient) {
        this.openPatientModal();
      }
    },
    openPatientModal() {
      this.showPatientModal = true;
      this.loadPatients();
    },
    onPatientConfirmed(patient) {
      this.selectedPatient = this.normalizePatient(patient);
    },
    async loadPatients() {
      if (!this.currentEmpresa?.id) {
        return;
      }
      this.loadingPatients = true;
      try {
        const res = await http.get(
          `/atendimento/tabelas?empresa_id=${this.currentEmpresa.id}`
        );
        const patients = res.data?.pacients || [];
        this.patientOptions = patients.map((p) => this.normalizePatient(p));
      } catch {
        this.patientOptions = [];
      } finally {
        this.loadingPatients = false;
      }
    },
  },
};
</script>
